import { useEffect, useMemo, useRef, useState } from 'react'
import {
  fillUpCar,
  getAppBalances,
  getFuelData,
  getStationConfiguration,
  ISession,
  Logout,
} from '../../../../services/apiService'
import {
  accountsAdapter,
  balancesAdapter,
  getDispensers,
  normaliseGuid,
  nozzlesAdapter,
  sameGuid,
  stationsAdapter,
  walletBalanceAdapter,
} from './adapters'
import {
  getErrorRule,
  getFuelLabel,
  getRetryCooldown,
  MAX_ATTEMPTS,
  MAX_LITERS,
  MIN_LITERS,
  NEARBY_RADIUS,
  readMessage,
  readStatus,
  resolveFailure,
} from './constants'
import { floorLiters, formatLiters, parseAmount, roundLiters } from './format'
import { findNearby } from './geo'
import {
  IAccountFuel,
  ICoords,
  IDispenserOption,
  IFuelOption,
  INearbyStation,
  INozzle,
  IRefuelAccount,
  IRefuelResult,
  IStation,
  RefuelStep,
} from './types'

/*
    `Status: 0` is the only success. The status is read through `readStatus`
    rather than off `res.Status`, because WOG Pay answers a refusal with a
    lower-cased `status` — see `resolveFailure`. `res.error === true` is the
    shared fetcher's transport failure, which carries no status at all.
 */
export const isOk = (res: any) =>
  !!res && res.error !== true && readStatus(res) === 0

/**
 * The token is no longer accepted. Status 5 is the service's own word for it;
 * `tokenRefused` is the page for app users' — AuthorizationAPP would not
 * record the link's token again, and its answer says why.
 */
export const isExpired = (res: any) =>
  !!res && (readStatus(res) === 5 || res.tokenRefused === true)

interface IWogPayOptions {
  visible: boolean
  onClose?: () => void
  /** The wallet the fuel is taken from. Nothing loads until it is known. */
  wallet?: string
  /** Phone and token the calls are signed with; the cabinet's own session when omitted. */
  session?: ISession
  /** Runs once fuel has been dispensed — the cabinet refreshes its balances. */
  onFuelled?: () => void
  /**
   * What a rejected token leads to. The cabinet logs out; the page for app
   * users has no session to end and reads the answer for what to tell the driver.
   */
  onExpired?: (res?: any) => void
  /** Where the driver is — the station within NEARBY_RADIUS is picked for them. */
  coords?: ICoords
}

/**
 * The whole WOG Pay flow: the three calls, everything derived from them, and
 * every transition between the two steps and the result screen. The modal, the
 * steps and the footer render what this returns and hold no flow state of
 * their own — which is what keeps the branching out of the components. The
 * page for app users runs the same hook with the session from its link.
 */
export const useWogPay = ({
  visible,
  onClose,
  wallet,
  session,
  onFuelled,
  onExpired,
  coords,
}: IWogPayOptions) => {
  const expire = (res?: any) => (onExpired ? onExpired(res) : Logout())

  const [step, setStep] = useState<RefuelStep>('station')
  const [loadingData, setLoadingData] = useState<boolean>(false)
  const [dataError, setDataError] = useState<string>()
  const [stations, setStations] = useState<IStation[]>([])
  /** The cabinet's: every account of the wallet, from FuelData. */
  const [fuelAccounts, setFuelAccounts] = useState<IRefuelAccount[]>([])
  const [accountId, setAccountId] = useState<string>()

  /*
      The page for app users gets its step-1 data in two parts: the stations
      from FuelData asked with no wallet, on arrival, and the leftovers from
      Mobile/Balances of the wallet chosen. FillUpCar still has to be told the
      account — the one the token is bound to, which only FuelData names, in
      ОстаткиПоАккаунтам — so it is learned from whichever answer carries it.
   */
  const [walletFuels, setWalletFuels] = useState<IAccountFuel[]>()
  const [loadingBalance, setLoadingBalance] = useState<boolean>(false)
  const [balanceError, setBalanceError] = useState<string>()
  const [appAccount, setAppAccount] = useState<{ id: string; name: string }>()
  const [query, setQuery] = useState<string>('')
  const [search, setSearch] = useState<string>('')
  const [station, setStation] = useState<IStation>()

  const [loadingConfig, setLoadingConfig] = useState<boolean>(false)
  const [configError, setConfigError] = useState<string>()
  const [nozzles, setNozzles] = useState<INozzle[]>([])
  const [dispenser, setDispenser] = useState<string>()
  const [fuelGuid, setFuelGuid] = useState<string>()

  const [amount, setAmount] = useState<string>('')

  const [submitting, setSubmitting] = useState<boolean>(false)
  const [result, setResult] = useState<IRefuelResult>()
  const [attempts, setAttempts] = useState<number>(0)
  const [cooldown, setCooldown] = useState<number>(0)

  /** On the page: one account, the token's, holding the wallet's leftovers. */
  const accounts = useMemo<IRefuelAccount[]>(() => {
    if (!session) return fuelAccounts
    if (!walletFuels || !walletFuels.length) return []
    return [
      { id: appAccount?.id || '', name: appAccount?.name || '', fuels: walletFuels },
    ]
  }, [session, fuelAccounts, walletFuels, appAccount])

  const account = session
    ? accounts[0]
    : accounts.find((item) => item.id === accountId)

  /* ----------------------------- step 1 data ----------------------------- */

  /*
      Both loads can be started again before the previous one has answered — a
      wallet switch, «Нова заправка», or simply a second station picked while
      the first is still in flight. Each call takes a ticket and drops its own
      response if a newer one has been issued since, so a slow answer can never
      overwrite the state of the request that replaced it.
   */
  const dataRequest = useRef(0)
  const balanceRequest = useRef(0)
  const configRequest = useRef(0)

  /* The wallet as of now, for an answer that lands after a switch. */
  const walletRef = useRef(wallet)
  walletRef.current = wallet

  /**
   * Stations and per-account leftovers arrive together, so one call feeds
   * both. The page for app users asks with `scope` '' to have the stations
   * at once; its leftovers come from `loadBalances`, and the rows here only
   * name the account the token is bound to.
   */
  const loadFuelData = (scope = wallet) => {
    const ticket = ++dataRequest.current
    setLoadingData(true)
    setDataError(undefined)
    return getFuelData(scope, session).then((res) => {
      if (ticket !== dataRequest.current) return
      setLoadingData(false)
      if (isExpired(res)) return expire(res)
      if (!isOk(res)) {
        setStations([])
        setFuelAccounts([])
        // Without this the empty list reads as «АЗК не знайдено», blaming the
        // search for what is actually a failed load.
        setDataError(readMessage(res) || 'Не вдалося завантажити дані заправки')
        return
      }
      setStations(stationsAdapter(res.Result['АЗК']))

      const list = accountsAdapter(res.Result['ОстаткиПоАккаунтам'])

      if (session) {
        if (list.length) {
          setAppAccount({ id: list[0].id, name: list[0].name })
        } else if (scope === '' && walletRef.current) {
          // The stations-only answer did not name the account; asked with the
          // wallet the driver has meanwhile chosen, FuelData does.
          loadFuelData(walletRef.current)
        }
        return
      }

      setFuelAccounts(list)
      // Default to the first account so the station list is usable at once.
      // A refetch keeps the account already chosen, unless it is gone.
      setAccountId((current) =>
        current && list.some((item) => item.id === current)
          ? current
          : list.length
          ? list[0].id
          : undefined
      )
    })
  }

  /** The leftovers of the chosen wallet on the page for app users. */
  const loadBalances = (target: string) => {
    if (!session) return
    const ticket = ++balanceRequest.current
    setLoadingBalance(true)
    setBalanceError(undefined)
    setWalletFuels(undefined)
    getAppBalances(target, session).then((res) => {
      if (ticket !== balanceRequest.current) return
      setLoadingBalance(false)
      if (isExpired(res)) return expire(res)
      if (!isOk(res)) {
        setBalanceError(readMessage(res) || 'Не вдалося завантажити залишки')
        return
      }
      setWalletFuels(balancesAdapter(res.Result))
    })
  }

  /* The cabinet: everything of the wallet in one go, on every switch. */
  useEffect(() => {
    if (!visible || session || !wallet) return
    loadFuelData()
  }, [visible, wallet]) // eslint-disable-line react-hooks/exhaustive-deps

  /* The page: the stations before any wallet is chosen, so the list is there on arrival. */
  useEffect(() => {
    if (!visible || !session) return
    loadFuelData('')
  }, [visible]) // eslint-disable-line react-hooks/exhaustive-deps

  /*
      The page: the wallet's leftovers on every switch. The token's account is
      learned from FuelData — when the stations-only call is still out, its
      handler asks again with this wallet if it has to; when it has answered
      without naming one, that is done here.
   */
  useEffect(() => {
    if (!visible || !session || !wallet) return
    loadBalances(wallet)
    if (!appAccount && !loadingData) loadFuelData(wallet)
  }, [visible, wallet]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Debounced, client-side — the payload has nothing else to filter on. */
  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim().toLowerCase()), 250)
    return () => clearTimeout(timer)
  }, [query])

  /** The station the driver is standing at, when the app said where that is. */
  const nearby = useMemo<INearbyStation | undefined>(
    () => (coords ? findNearby(stations, coords, NEARBY_RADIUS) : undefined),
    [stations, coords]
  )

  /* The matched station leads the list, search or no search. */
  const ordered = useMemo(
    () =>
      nearby
        ? [
            nearby.station,
            ...stations.filter((item) => item.code !== nearby.station.code),
          ]
        : stations,
    [stations, nearby]
  )

  const filtered = useMemo(
    () =>
      search
        ? ordered.filter((item) => item.search.indexOf(search) !== -1)
        : ordered,
    [ordered, search]
  )

  /* Picked on arrival; the driver only confirms it with «Далі». */
  useEffect(() => {
    if (nearby && !station) setStation(nearby.station)
  }, [nearby]) // eslint-disable-line react-hooks/exhaustive-deps

  /** The wallet's own balance — its accounts' leftovers summed per fuel. */
  const walletBalance = useMemo(() => walletBalanceAdapter(accounts), [accounts])

  /* ----------------------------- step 2 data ----------------------------- */

  const loadConfiguration = (target: IStation) => {
    const ticket = ++configRequest.current
    setLoadingConfig(true)
    setConfigError(undefined)
    setNozzles([])
    getStationConfiguration(target.code, session).then((res) => {
      if (ticket !== configRequest.current) return
      setLoadingConfig(false)
      if (isExpired(res)) return expire(res)
      if (!isOk(res)) {
        setConfigError(readMessage(res) || 'Не вдалося отримати конфігурацію АЗК')
        return
      }
      setNozzles(nozzlesAdapter(res.Result?.fuelstationData))
    })
  }

  /**
   * The station's dispensers, each tested against the account: a dispenser
   * offering nothing the account can take is shown but never selectable, the
   * same rule the fuel tiles use one level down.
   */
  const dispensers = useMemo<IDispenserOption[]>(
    () =>
      getDispensers(nozzles).map((id) => ({
        id,
        available:
          !!account &&
          nozzles.some(
            (item) =>
              item.dispenser === id &&
              account.fuels.some(
                (fuel) =>
                  sameGuid(fuel.id, item.goodsGuid) &&
                  fuel.quantity >= MIN_LITERS
              )
          ),
      })),
    [nozzles, account]
  )

  /** Fuels of the chosen dispenser, deduped by GUID and joined with balances. */
  const fuels = useMemo<IFuelOption[]>(() => {
    if (!dispenser || !account) return []
    const seen: { [guid: string]: boolean } = {}
    return nozzles
      .filter((item) => item.dispenser === dispenser)
      // Deduped on the normalised GUID, the same way the account is matched —
      // otherwise two spellings of one fuel would put up two tiles.
      .filter((item) => {
        const key = normaliseGuid(item.goodsGuid)
        if (seen[key]) return false
        seen[key] = true
        return true
      })
      .map((item) => {
        const owned = account.fuels.find((fuel) =>
          sameGuid(fuel.id, item.goodsGuid)
        )
        const label = getFuelLabel(item.goodsCode, item.goodsName)
        if (!owned || !owned.quantity) {
          return { ...item, label, available: false, reason: 'немає на рахунку' }
        }
        if (owned.quantity < MIN_LITERS) {
          return {
            ...item,
            label,
            balance: owned.quantity,
            available: false,
            reason: `менше ${MIN_LITERS} л`,
          }
        }
        return { ...item, label, balance: owned.quantity, available: true }
      })
  }, [nozzles, dispenser, account])

  /** Not one dispenser overlaps the account — the whole station is useless. */
  const stationHasFuel = dispensers.some((item) => item.available)

  /** True once the configuration call settled without giving a usable fuel. */
  const blocked = !loadingConfig && !configError && !stationHasFuel

  const fuel = fuels.find((item) => item.goodsGuid === fuelGuid)
  const max = fuel ? floorLiters(fuel.balance || 0) : 0

  const liters = roundLiters(parseAmount(amount))

  const validation = useMemo(() => {
    if (!fuel || !amount) return undefined
    if (liters < MIN_LITERS) return `Мінімум — ${MIN_LITERS} л`
    if (liters > max) return `На акаунті лише ${formatLiters(max)} л`
    if (liters > MAX_LITERS) return `Максимум — ${formatLiters(MAX_LITERS)} л`
    return undefined
  }, [fuel, amount, liters, max])

  const canSubmit =
    !!fuel &&
    !validation &&
    liters >= MIN_LITERS &&
    liters <= max &&
    liters <= MAX_LITERS

  /* ------------------------------ transitions ---------------------------- */

  const resetAmount = () => setAmount('')

  const resetStation = () => {
    setStation(undefined)
    setNozzles([])
    setConfigError(undefined)
    setDispenser(undefined)
    setFuelGuid(undefined)
    resetAmount()
  }

  const chooseAccount = (id: string) => {
    setAccountId(id)
    setQuery('')
    resetStation()
  }

  const chooseStation = (target: IStation) => {
    setStep('amount')
    // «Назад» leaves the station selected, so «Далі» can bring the manager
    // straight back to a configuration that is already loaded.
    if (station?.code === target.code && nozzles.length) return
    setStation(target)
    setDispenser(undefined)
    setFuelGuid(undefined)
    resetAmount()
    loadConfiguration(target)
  }

  /*
      Neither of these clears the amount. The manager may type it before
      picking a fuel, and each pick changes the ceiling rather than
      invalidating the figure — `validation` re-tests it against the new fuel
      and blocks «Заправити» while it does not fit, which says more than
      silently emptying the field would.
   */
  const chooseDispenser = (value: string) => {
    setDispenser(value)
    setFuelGuid(undefined)
  }

  const chooseFuel = (value: IFuelOption) => {
    setFuelGuid(value.goodsGuid)
  }

  const applyTemplate = (value: number) => setAmount(String(floorLiters(value)))

  const reset = () => {
    setStep('station')
    setStations([])
    setFuelAccounts([])
    setAccountId(undefined)
    setWalletFuels(undefined)
    setBalanceError(undefined)
    setDataError(undefined)
    setQuery('')
    setSearch('')
    setResult(undefined)
    setAttempts(0)
    setCooldown(0)
    resetStation()
  }

  const close = () => {
    if (onClose) onClose()
    reset()
  }

  /** «Далі» — only reachable once «Назад» has left a station selected. */
  const next = () => station && chooseStation(station)

  /**
   * «Назад» — one step, nothing cleared. From the amount step to the station;
   * from a failure back to the amount step with the station, pump, fuel and
   * figure as they were, so the driver can change one of them — or step back
   * once more for another account or wallet — instead of starting over.
   */
  const back = () => {
    if (step === 'result') {
      setResult(undefined)
      setStep('amount')
      return
    }
    setStep('station')
  }

  const retryConfiguration = () => station && loadConfiguration(station)

  const chooseOtherStation = () => {
    resetStation()
    setStep('station')
  }

  /**
   * «Нова заправка» — keep the account and start from the station again. The
   * balances just changed, so they are refetched rather than reused: they are
   * the ceiling the next amount is validated against.
   */
  const startOver = () => {
    setResult(undefined)
    setAttempts(0)
    setCooldown(0)
    resetStation()
    setStep('station')
    if (!session) {
      loadFuelData()
    } else if (wallet) {
      loadBalances(wallet)
    }
  }

  /* -------------------------------- submit ------------------------------- */

  const submit = () => {
    if (!station || !account || !fuel || !dispenser) return
    // antd already swallows clicks on a loading button, but a second order
    // cannot be taken back — FillUpCar has no idempotency key — so the guard
    // belongs here too, where every caller passes.
    if (submitting) return
    setSubmitting(true)
    setAttempts((value) => value + 1)
    fillUpCar(
      {
        wallet,
        account: account.id,
        stationCode: station.code,
        fuel: fuel.goodsGuid,
        dispenser,
        amount: liters,
      },
      session
    ).then((res) => {
      setSubmitting(false)
      setStep('result')
      if (isExpired(res)) return expire(res)

      if (isOk(res)) {
        setResult({
          ok: true,
          talon: res.Result?.['Талон'] || '',
          dispenser,
          fuelName: fuel.label,
          amount: liters,
        })
        // The account has less fuel now — AC 10.
        if (onFuelled) onFuelled()
        return
      }

      const { status, error, message } = resolveFailure(res)
      const rule = getErrorRule(status, error)
      setResult({
        ok: false,
        message: message || 'Заправку не розпочато. Спробуйте пізніше.',
        status,
        error,
        action: rule.action,
      })
      if (rule.action === 'retry') setCooldown(getRetryCooldown(rule))
    })
  }

  /* FillUpCar has no idempotency key, so retries are paced and capped. */
  useEffect(() => {
    if (!cooldown) return
    const timer = setTimeout(() => setCooldown(cooldown - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  const canRetry = attempts < MAX_ATTEMPTS

  /** Back to the screen the failure can be fixed on, with that field cleared. */
  const recover = () => {
    if (!result || result.ok) return
    setResult(undefined)
    if (result.action === 'station') {
      resetStation()
      setStep('station')
      return
    }
    if (result.action === 'amount') resetAmount()
    setStep('amount')
  }

  return {
    /* step 1 */
    step,
    loadingData,
    dataError,
    retryFuelData: () => loadFuelData(session ? '' : undefined),
    accounts,
    account,
    walletBalance,
    /** The page for app users: the wallet's leftovers being fetched, or why they could not be. */
    loadingBalance,
    balanceError,
    stations,
    filtered,
    nearby,
    query,
    /** True while a debounced query is in effect, so the count matches the list. */
    searching: !!search,
    setQuery,
    station,

    /* step 2 */
    loadingConfig,
    configError,
    blocked,
    /** Distinguishes «немає пального» from a station that returned nothing. */
    hasNozzles: !!nozzles.length,
    dispensers,
    dispenser,
    fuels,
    fuel,
    amount,
    setAmount,
    liters,
    max,
    validation,
    canSubmit,

    /* result */
    submitting,
    result,
    canRetry,
    cooldown,

    /* transitions */
    chooseAccount,
    chooseStation,
    chooseDispenser,
    chooseFuel,
    applyTemplate,
    next,
    back,
    retryConfiguration,
    chooseOtherStation,
    submit,
    recover,
    startOver,
    close,
  }
}

/** What the modal, its steps and its footer are handed. */
export type IWogPay = ReturnType<typeof useWogPay>
